import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { SpaceAccountPostHogFeatureFlagIds } from "./types";
import { action, computed, makeObservable } from "mobx";
import { asyncResultModule } from "@/modules/async-result";
import { AsyncResult } from "@/modules/async-result/types";
import { BaseError } from "@/domains/errors";
import { errorHandlingModule } from "@/modules/error-handling";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import pTimeout from "p-timeout";
import { api } from "@/modules/api";
import { Duration } from "luxon";
import {
  parseSpaceAccountFeatureFlags,
  SpaceAccountFeatureFlags,
} from "@/store/feature-flags/schemas";

const FEATURE_FLAGS_REQUEST_TIMEOUT = Duration.fromObject({ seconds: 5 });
const DEFAULT_FEATURE_FLAG_STATE: SpaceAccountFeatureFlags = {};

export class AppStoreSpaceAccountFeatureFlagsStore extends AppSubStore {
  private flags: SpaceAccountFeatureFlags = {};

  // The AppStore waits on this ready state to set its own ready state.
  // Hence, we block the app from rendering until the flags are loaded.
  public readyState: AsyncResult<boolean> = asyncResultModule.setLoading();

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<
      AppStoreSpaceAccountFeatureFlagsStore,
      | "flags"
      | "initialize"
      | "hydrateFromDb"
      | "getDbFlags"
      | "refreshFromRemote"
      | "setFlags"
      | "setReady"
    >(this, {
      flags: true,
      readyState: true,
      initialize: false,
      hydrateFromDb: false,
      getDbFlags: false,
      refreshFromRemote: false,
      getFlagStatus: false,
      setFlags: action,
      setReady: action,
      featureFlags: computed,
    });
  }

  /**
   * NOTE: The app will remain in a loading state until the feature flags are loaded.
   * So, it is important that this completes quickly.
   *
   * We set this up so that:
   * - The server has `FEATURE_FLAGS_REQUEST_TIMEOUT` amount of time to respond.
   * - If the server does not respond, we fallback to the feature flags stored in the DB.
   * - If there are no DB feature flags, we fall back to the "default" feature flag state.
   *
   * Note that if we've already fetched the flags, `readyState.data` will be set, so
   * this will no-op. (If necessary, we can manually refresh the flags by calling `refreshFromRemote`.)
   */
  async initialize(): Promise<void> {
    if (this.readyState.data) {
      return;
    }

    if (!this.store.isMemDbReady) {
      throw new BaseError({
        message:
          "[AppStoreSpaceAccountFeatureFlagsStore] MemDB was not ready during initialize; cannot load feature flags",
      });
    }

    await this.hydrateFromDb();
    await this.refreshFromRemote();

    this.setReady();
  }

  private async hydrateFromDb(): Promise<void> {
    const dbFlags = await this.getDbFlags();
    this.setFlags(dbFlags ?? DEFAULT_FEATURE_FLAG_STATE);
  }

  private async refreshFromRemote(): Promise<void> {
    try {
      const response = await pTimeout(api.get("/v2/feature-flags", {}), {
        milliseconds: FEATURE_FLAGS_REQUEST_TIMEOUT.toMillis(),
        fallback: () => {
          throw new BaseError({
            message: "[AppStoreSpaceAccountFeatureFlagsStore] Feature flags request timed out",
            info: { timeoutMs: FEATURE_FLAGS_REQUEST_TIMEOUT.toMillis() },
          });
        },
      });

      if (response.error || !response.data) {
        throw new BaseError({
          message:
            "[AppStoreSpaceAccountFeatureFlagsStore] Feature flags request failed or returned no data",
          info: { response: objectModule.safeAsJson(response) },
        });
      }

      const flags = parseSpaceAccountFeatureFlags({
        flags: response.data.flags,
        errorMessage:
          "[AppStoreSpaceAccountFeatureFlagsStore] Invalid feature flags payload from API",
      });

      this.setFlags(flags);

      try {
        await this.store.memDb.settings.setFeatureFlags(flags);
      } catch (unknownErr) {
        const err = errorHandlingModule.parseError({ unknownError: unknownErr });

        logger.warn({
          message: "[AppStoreSpaceAccountFeatureFlagsStore] Failed to persist DB feature flags",
          info: { err: objectModule.safeErrorAsJson(err) },
        });
      }
    } catch (unknownErr) {
      const err = errorHandlingModule.parseError({ unknownError: unknownErr });
      logger.warn({
        message: "[AppStoreSpaceAccountFeatureFlagsStore] Falling back to DB feature flags",
        info: { err: objectModule.safeErrorAsJson(err) },
      });
    }
  }

  private async getDbFlags(): Promise<SpaceAccountFeatureFlags | null> {
    try {
      const flags = await this.store.memDb.settings.getFeatureFlags();

      if (!flags) {
        return null;
      }

      return parseSpaceAccountFeatureFlags({
        flags,
        errorMessage: "[AppStoreSpaceAccountFeatureFlagsStore] Invalid feature flags payload in DB",
      });
    } catch (unknownErr) {
      const err = errorHandlingModule.parseError({ unknownError: unknownErr });

      logger.warn({
        message: "[AppStoreSpaceAccountFeatureFlagsStore] Failed to read DB feature flags",
        info: { err: objectModule.safeErrorAsJson(err) },
      });

      return null;
    }
  }

  private setFlags(flags: SpaceAccountFeatureFlags): void {
    this.flags = { ...flags };
  }

  private setReady(): void {
    this.readyState = asyncResultModule.setReady(true);
  }

  /**
   * Feature flags are "locked in" after first access by design.
   * They are loaded during boot and will not refresh again until the page reloads.
   */
  get featureFlags(): SpaceAccountFeatureFlags {
    return this.flags;
  }

  /**
   * Returns the status of a feature flag. Returns `undefined` if the backend doesn't know
   * the status of this flag for this user yet.
   */
  getFlagStatus(flag: SpaceAccountPostHogFeatureFlagIds): boolean | undefined {
    const val = this.featureFlags[flag];
    return val ?? undefined;
  }
}
